import React, {useContext, useEffect, useState} from 'react';
import {Button, Dialog, DialogBody, DialogFooter, DialogHeader} from '@material-tailwind/react';
import { ProjectInfo } from '../../../models/ProjectForm';
import projectFormService from '../../../services/forms/projectForm.service';
import ProjectCard from '../../../components/admin/ProjectCard';
import exportsService from '../../../services/exports.service';
import periodsService from '../../../services/periods.service';
import { Period } from '../../../models/Periods';
import usePagination from "../../../hooks/usePagination";
import './ProjectsList.css';
import AppTitleContext from "../../../context/AppTitleContext";
import {useForm} from "react-hook-form";
import CustomInput from "../../../components/shared/Input/CustomInput";
import {faArrowRight} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import certificatesService from "../../../services/certificates.service";
import {CertificateRange} from "../../../models/CertificateRequest";
import projectConstancyService from '../../../services/projectConstancy.service';

type CertificateAction = 'download' | 'send';
type AdvisorCertificateProgress = {
    generated: number;
    total: number;
};

function ProjectList() {
    const { register, reset, formState: { errors }, handleSubmit } = useForm<CertificateRange>();
    const [projects, setProjects] = useState([] as ProjectInfo[]);
    const [showFolioRangeForm, setShowFolioRangeForm] = useState(false);
    const [certificateAction, setCertificateAction] = useState<CertificateAction>('download');
    const [currentPeriod, setCurrentPeriod] = useState<Period>({id: '[...]'} as Period);
    const { getPagination, currentData } = usePagination<ProjectInfo[]>(projects, 4);
    const { updateAppTitle } = useContext(AppTitleContext);
    const [userOption, setUserOption] = useState('1');
    const [advisorCertificateProgress, setAdvisorCertificateProgress] = useState<AdvisorCertificateProgress>({ generated: 0, total: 0 });
    const [isLoadingCertificateProgress, setIsLoadingCertificateProgress] = useState(false);

    async function getProjects() {
        let response = await projectFormService.getAllProjects();
        setProjects(response?.projects || []);
    }

    const getPeriods = async () => {
        const response = await periodsService.getCurrentPeriod();
        setCurrentPeriod(response);
    }

    const getAdvisorCertificatesProgress = async (periodId?: string) => {
        if (!periodId || periodId === '[...]') {
            return;
        }

        setIsLoadingCertificateProgress(true);
        try {
            const response = await certificatesService.getAdvisorCertificatesProgress(periodId);
            setAdvisorCertificateProgress({
                generated: response?.generated ?? 0,
                total: response?.total ?? 0,
            });
        } finally {
            setIsLoadingCertificateProgress(false);
        }
    }

    const certificatesGenerator = {
        handleShowDialog(action?: CertificateAction) {
            if (action) {
                setCertificateAction(action);
            }
            setShowFolioRangeForm(!showFolioRangeForm);
        },
        cancelProcess() {
            reset();
            certificatesGenerator.handleShowDialog();
        },
        async handleFormSubmit(data: CertificateRange) {
            const payload = {
                option: { period: currentPeriod.id },
                range: { min: parseInt(data.min), max: parseInt(data.max) }
            };
            
            certificatesGenerator.handleShowDialog();
            if (certificateAction === 'download') {
                await certificatesService.generateCertificates(payload);
            } else if (certificateAction === 'send') {
                await certificatesService.sendCertificates(payload);
            }

            await getAdvisorCertificatesProgress(currentPeriod.id);
        }
    };

    const handleSendConstancies = () => {
        // Mostrar alerta de confirmación
        if (!window.confirm(`¿Estás seguro de que deseas enviar las constancias del periodo ${currentPeriod.id} por correo electrónico?`)) {
            return;
        }

        const payload = {
            period: currentPeriod.id
        };
        projectConstancyService.sendConstancies(payload);
    };

    // Función para obtener los textos según la acción
    const getModalTexts = () => {
        if (certificateAction === 'download') {
            return {
                title: 'Descargar certificados',
                description: 'Ingresa el rango de folios para descargar:',
                buttonText: 'Descargar',
                buttonColor: 'green' as const
            };
        } else {
            return {
                title: 'Enviar certificados',
                description: 'Ingresa el rango de folios para enviar por correo:',
                buttonText: 'Enviar',
                buttonColor: 'green' as const
            };
        }
    };

    const modalTexts = getModalTexts();

    useEffect(() => {
        getProjects();
        getPeriods();
        updateAppTitle([{title: 'Proyectos'}]);
    }, []);

    useEffect(() => {
        if (userOption === '1') {
            getAdvisorCertificatesProgress(currentPeriod.id);
        }
    }, [currentPeriod.id, userOption]);

    return (
        <div className='projects'>
            <section className='projects-container'>
                <div className="flex justify-between items-center w-full mr-2">
                    <div>
                        Cantidad total: {projects.length}
                    </div>
                    <div className='flex gap-2'>
                        {/* Exportar datos de proyectos */}
                        <Button color='blue' onClick={() => {
                            exportsService.downloadProjectDataCSV()
                        }} placeholder=''>Exportar {currentPeriod.id}</Button>
                        {/* Seleccionador para el tipo de usuario */}
                        <select id="user-options" value={userOption} className='bg-yellow-700 p-2 rounded-lg text-black font-semibold' onChange={(e) => setUserOption(e.target.value)}>
                            <option value='1'>Asesores</option>
                            <option value='2'>Estudiantes</option>
                        </select>
                        {userOption === '1' ? (
                        <>
                            {/* Descargar certificados de asesores */}
                            <Button color='blue' onClick={() => certificatesGenerator.handleShowDialog('download')} placeholder=''>
                                Descargar Certificados {currentPeriod.id}
                            </Button>
                            {/* Enviar certificados de asesores */}
                            <Button color='blue' onClick={() => certificatesGenerator.handleShowDialog('send')} placeholder=''>
                                Enviar Certificados {currentPeriod.id}
                            </Button>
                        </>
                        ) : (
                        <>
                            {/* Descargar constancias de estudiantes */}
                            <Button color='blue' onClick={() => projectConstancyService.downloadConstancies({ option: { period: currentPeriod.id } })} placeholder=''>
                                Descargar Constancias {currentPeriod.id}
                            </Button>
                            {/* Enviar constancias de estudiantes */}
                            <Button color='blue' onClick={() => handleSendConstancies()} placeholder=''>
                                Enviar Constancias {currentPeriod.id}
                            </Button>
                        </>
                        )}
                    </div>
                </div>
                {currentData?.map((project) => <ProjectCard project={project} key={project.uuid}/>)}
            </section>
            <div className='pagination'>
                {getPagination()}
            </div>
            <Dialog style={{zIndex:20}} open={showFolioRangeForm} handler={certificatesGenerator.handleShowDialog} placeholder=''>
                <DialogHeader placeholder=''>{modalTexts.title}</DialogHeader>
                <DialogBody style={{paddingTop:0}} placeholder=''>
                    <p className='text-center mb-3 text-sm text-blue-gray-700 font-semibold'>
                        {isLoadingCertificateProgress
                            ? 'Certificados generados: calculando...'
                            : `Certificados generados: ${advisorCertificateProgress.generated}/${advisorCertificateProgress.total} asesores`}
                    </p>
                    <div className='flex items-center gap-3'>
                        {modalTexts.description}
                        <CustomInput type='number' errors={errors} label="Inicio" field="min" register={register}
                            rules={{required: true}}/>
                        <FontAwesomeIcon icon={faArrowRight} color='#03A300' size="lg"/>
                        <CustomInput type='number' errors={errors} label="Fin" field="max" register={register}
                            rules={{required: true }}/>
                    </div>
                </DialogBody>
                <DialogFooter placeholder=''>
                    <div className="flex items-center gap-2">
                        <Button variant="text" color="red" onClick={certificatesGenerator.cancelProcess} placeholder=''>
                            Cancelar
                        </Button>
                        <Button 
                            variant="gradient" 
                            color={modalTexts.buttonColor}
                            onClick={handleSubmit((d) => certificatesGenerator.handleFormSubmit(d))} 
                            placeholder=''
                        >
                            {modalTexts.buttonText}
                        </Button>
                    </div>
                </DialogFooter>
            </Dialog>
        </div>
    );
}

export default ProjectList;